/**
 * @file index.tsx
 *
 * @fileoverview User Avatar component.
 */

import { cva } from "class-variance-authority";
import { UserAvatarImage } from "./01-user-avatar-image";
import styles from "./Avatar.module.scss";

const avatarWrapper = cva([styles.avatarWrapper], {
  variants: {
    status: {
      active: styles.avatarWrapperActive,
      hidden: styles.avatarWrapperHidden,
      inactive: styles.avatarWrapperInactive,
    },
    size: {
      small: styles.avatarWrapperSmall,
      medium: styles.avatarWrapperMedium,
    },
  },
  defaultVariants: {
    size: "medium",
  },
});

export interface AvatarProps {
  /**
   * Alt text for the user image.
   */
  alt: string;
  /**
   * Title for the user image.
   */
  title: string;
  /**
   * Size of the image.
   */
  size?: "small" | "medium";
  /**
   * Src of the image.
   */
  src?: string;
  /**
   * Status.
   */
  status?: "active" | "hidden" | "inactive";
  /**
   * Extra class names to override styles.
   */
  className?: string;
}

/**
 * Display a user avatar image.
 */
export default function Avatar({
  status,
  size = "medium",
  title,
  alt,
  src,
  className = "",
  ...props
}: AvatarProps) {
  return (
    <div
      className={`
      ${className}
      ${avatarWrapper({ status, size })}
    `.trim()}
    >
      <UserAvatarImage
        src={src}
        alt={alt ?? ""}
        title={title}
        size={size}
        {...props}
      />
    </div>
  );
}

export { Avatar };
